/**
 * Onde o rastreador está — o veículo em que está instalado, o mapa do que ele andou, as paradas
 * que ficaram, e há quanto tempo ele não fala.
 *
 * A tela abre já sabendo do que está falando: pergunta a instalação vigente e carrega o trajeto
 * daquele veículo sozinha. Antes ela nascia vazia e exigia que o operador buscasse à mão o veículo
 * do próprio aparelho que estava olhando — o dado existia e a tela fingia não saber.
 *
 * O silêncio é primeira classe. Um rastreador que reporta a cada 20 segundos e está há duas horas
 * calado não está "sem novidade", está com problema; por isso o cabeçalho diz há quanto tempo foi a
 * última posição, e não só qual foi.
 *
 * ## A tela aberta acompanha
 *
 * Um mapa de rastreamento que congela no instante em que foi aberto obriga quem está acompanhando
 * um veículo a apertar F5 — e recarregar a página inteira para saber de um ponto novo é caro para
 * o servidor e pior para quem olha, porque perde o zoom, o arraste e o lugar onde estava a atenção.
 * Aqui a tela pergunta sozinha, a cada quinze segundos, **só o que chegou depois do último ponto
 * que ela já tem**: quase sempre a resposta é uma lista vazia, e quando não é, a linha cresce sem
 * que nada mais se mexa.
 *
 * Duas contenções, porque uma tela que pergunta sozinha é uma tela que pode virar peso morto:
 * ela para quando a aba deixa de estar visível (ninguém acompanha o que não está vendo) e usa o
 * modo `SILENCIOSO` do cliente HTTP, senão a barra de progresso do topo piscaria a cada tique e a
 * página pareceria estar sempre carregando.
 */
import { AlertTriangle, Car, MapPin, Radio } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  ComboBuscaEntidade,
  type ConfigBusca,
  type ItemBusca,
} from '@/components/crud/ComboBuscaEntidade';
import {
  type EstadoRota,
  MapaTrajeto,
  type ParadaMapa,
  type PontoTrajeto,
} from '@/components/crud/MapaTrajeto';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Chip, Spinner } from '@/components/ui/misc';
import { api, SILENCIOSO } from '@/lib/api';
import { CHAVE_MAPS, mapIdEhDemo } from '@/lib/mapas';

/** De quanto em quanto tempo a tela aberta pergunta por posição nova. */
const INTERVALO_VIVO_MS = 15_000;
/** As paradas nascem do worker de análise, não da posição: perguntar por elas todo tique seria
 * gastar consulta para receber sempre a mesma lista. Uma vez por minuto basta. */
const TIQUES_POR_RECARGA_DE_PARADAS = 4;
/** Teto de pontos por janela — é o máximo que a rota aceita. Chegando nele, a tela avisa em vez de
 * deixar o operador achar que o trajeto começa onde na verdade a lista foi cortada. */
const LIMITE_TRAJETO = 5000;

const BUSCA_VEICULO: ConfigBusca = {
  endpoint: '/cadastro/veiculos',
  rotulo: (v) => String(v.placa || v.modelo || 'Veículo'),
  sub: (v) => [v.marca, v.modelo].filter(Boolean).map(String).join(' ') || null,
  placeholder: 'Buscar veículo por placa ou modelo…',
  camposRapido: [
    { chave: 'placa', rotulo: 'Placa', tipo: 'texto' },
    { chave: 'modelo', rotulo: 'Modelo', tipo: 'texto' },
  ],
};

type Instalacao = {
  instalacao_id: string;
  veiculo_id: string;
  placa: string | null;
  marca: string | null;
  modelo: string | null;
  desde: string;
};

type Estado = {
  nunca_reportou: boolean;
  total_posicoes: number;
  em?: string;
  lat?: number;
  lon?: number;
  kmh?: number | null;
  silencio_seg?: number;
  /** Mediana dos intervalos REAIS entre as últimas posições — o que o aparelho pratica. */
  cadencia_seg?: number | null;
  cadencia_amostras?: number;
  /** O que o SMS de configuração pede — o que o aparelho DEVERIA praticar. */
  intervalo_alvo_seg?: number;
};

/** As janelas oferecidas. `null` = tudo o que houver — útil numa base semeada, honesto numa real.
 *
 * A `chave` é o que aparece na URL (`?janela=7d`). Esta barra tem cara de aba e é usada como aba,
 * então segue a mesma regra: recarregar não pode devolver o recorte errado, e mandar «olha o
 * trajeto dele nos últimos 7 dias» tem de ser um link, não uma instrução de dois passos.
 *
 * Query e não fragmento, ao contrário das abas: a janela é um FILTRO sobre o conteúdo da aba, não
 * um lugar dentro dela — e assim os dois convivem sem disputar o mesmo pedaço do endereço.
 */
const JANELAS: { chave: string; rotulo: string; horas: number | null }[] = [
  { chave: '24h', rotulo: '24 h', horas: 24 },
  { chave: '7d', rotulo: '7 dias', horas: 24 * 7 },
  { chave: '30d', rotulo: '30 dias', horas: 24 * 30 },
  { chave: 'tudo', rotulo: 'Tudo', horas: null },
];
const JANELA_PADRAO = JANELAS[3];

/** «a cada 20 s», «a cada 4 min» — a unidade em que se pensa uma cadência. */
function cadenciaHumana(seg: number): string {
  if (seg < 90) return `${seg} s`;
  if (seg < 5400) return `${Math.round(seg / 60)} min`;
  return `${Math.round(seg / 3600)} h`;
}

/**
 * A cadência praticada está longe da pedida?
 *
 * O dobro é a fronteira, e não uma diferença qualquer: o aparelho não acerta o intervalo no
 * cronômetro — ele atrasa quando a rede engasga, e adianta quando manda alarme junto. Chamar 26 s
 * de «fora de configuração» treinaria o operador a ignorar o aviso, que é o pior desfecho para um
 * aviso. Já o dobro não é ruído: é outro intervalo.
 */
function longeDoAlvo(cadencia: number, alvo: number): boolean {
  return cadencia > alvo * 2;
}

function silencioHumano(seg: number): string {
  if (seg < 120) return `há ${seg}s`;
  if (seg < 7200) return `há ${Math.round(seg / 60)} min`;
  if (seg < 172800) return `há ${Math.round(seg / 3600)} h`;
  return `há ${Math.round(seg / 86400)} dias`;
}

export function PainelRastreadorInstalacao({ rastreadorId }: { rastreadorId: string }) {
  const [instalacao, setInstalacao] = useState<Instalacao | null>(null);
  const [estado, setEstado] = useState<Estado | null>(null);
  const [pontos, setPontos] = useState<PontoTrajeto[]>([]);
  const [paradas, setParadas] = useState<ParadaMapa[]>([]);
  // A janela escolhida mora na URL, como a aba. Ausente = «Tudo»: um filtro no padrão é a mesma
  // coisa que filtro nenhum, e escrever `?janela=tudo` em toda ficha seria sujeira sem informação.
  const { pathname, search, hash } = useLocation();
  const navegar = useNavigate();
  const janelaEscolhida =
    JANELAS.find((j) => j.chave === new URLSearchParams(search).get('janela')) ?? JANELA_PADRAO;
  const janela = janelaEscolhida.horas;

  const escolherJanela = useCallback(
    (chave: string) => {
      const parametros = new URLSearchParams(search);
      if (chave === JANELA_PADRAO.chave) parametros.delete('janela');
      else parametros.set('janela', chave);
      const query = parametros.toString();
      // O `hash` viaja junto: trocar a janela não pode fechar a aba em que a pessoa está.
      navegar(`${pathname}${query ? `?${query}` : ''}${hash}`);
    },
    [pathname, search, hash, navegar],
  );
  const [carregando, setCarregando] = useState(true);
  const [mapaCaiu, setMapaCaiu] = useState(false);
  const [rota, setRota] = useState<EstadoRota | null>(null);

  const [veiculo, setVeiculo] = useState<ItemBusca | null>(null);
  const [em, setEm] = useState('');
  const [instalando, setInstalando] = useState(false);

  const aoFalharMapa = useCallback(() => setMapaCaiu(true), []);
  const aoEstadoRota = useCallback((e: EstadoRota) => setRota(e), []);

  /** O instante do último ponto que a tela já tem — é o `desde` do próximo tique. Num ref, e não
   * no estado, porque o tique precisa do valor ATUAL sem que mudá-lo recrie o cronômetro. */
  const ultimoEm = useRef<string | null>(null);

  /** A janela em querystring, montada num lugar só para a carga e o tique não divergirem. */
  const filtroJanela = useCallback(
    () => (janela === null ? [] : [`horas=${janela}`]),
    [janela],
  );

  const carregar = useCallback(async () => {
    setCarregando(true);
    const [rInst, rEstado] = await Promise.all([
      api.get<Instalacao | null>(`/cadastro/rastreadores/${rastreadorId}/instalacao`),
      api.get<Estado>(`/cadastro/rastreadores/${rastreadorId}/estado`),
    ]);
    if (rEstado.ok) setEstado(rEstado.dados);
    const inst = rInst.ok ? rInst.dados : null;
    setInstalacao(inst);
    if (!inst) {
      setPontos([]);
      setParadas([]);
      ultimoEm.current = null;
      setCarregando(false);
      return;
    }
    const q = [`limite=${LIMITE_TRAJETO}`, ...filtroJanela()].join('&');
    const [rTraj, rParadas] = await Promise.all([
      api.get<PontoTrajeto[]>(`/cadastro/rastreadores/veiculos/${inst.veiculo_id}/trajeto?${q}`),
      api.get<ParadaMapa[]>(`/cadastro/rastreadores/veiculos/${inst.veiculo_id}/paradas`),
    ]);
    if (rTraj.ok) {
      setPontos(rTraj.dados);
      ultimoEm.current = rTraj.dados.at(-1)?.em ?? null;
    }
    if (rParadas.ok) setParadas(rParadas.dados);
    setCarregando(false);
  }, [rastreadorId, filtroJanela]);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  // O tique: só o que chegou depois do último ponto conhecido. Silencioso, para não acender a
  // barra de progresso do topo — ela anuncia o que o operador pediu, não a manutenção da tela.
  const veiculoId = instalacao?.veiculo_id;
  const tique = useCallback(
    async (recarregarParadas: boolean) => {
      if (!veiculoId) return;
      const partes = [`limite=${LIMITE_TRAJETO}`, ...filtroJanela()];
      if (ultimoEm.current) partes.push(`desde=${encodeURIComponent(ultimoEm.current)}`);
      const [rTraj, rEstado] = await Promise.all([
        api.get<PontoTrajeto[]>(
          `/cadastro/rastreadores/veiculos/${veiculoId}/trajeto?${partes.join('&')}`,
          SILENCIOSO,
        ),
        api.get<Estado>(`/cadastro/rastreadores/${rastreadorId}/estado`, SILENCIOSO),
      ]);
      if (rEstado.ok) setEstado(rEstado.dados);
      if (rTraj.ok && rTraj.dados.length) {
        ultimoEm.current = rTraj.dados.at(-1)?.em ?? ultimoEm.current;
        // Concatena em vez de substituir: o pedido trouxe só o pedaço novo. O corte pelo teto
        // preserva o FIM, que é onde o carro está — perder o começo de um trajeto longo é o mal
        // menor diante de deixar a lista crescer sem limite numa tela que fica aberta o dia todo.
        setPontos((antes) => [...antes, ...rTraj.dados].slice(-LIMITE_TRAJETO));
      }
      if (recarregarParadas) {
        const r = await api.get<ParadaMapa[]>(
          `/cadastro/rastreadores/veiculos/${veiculoId}/paradas`,
          SILENCIOSO,
        );
        if (r.ok) setParadas(r.dados);
      }
    },
    [veiculoId, rastreadorId, filtroJanela],
  );

  useEffect(() => {
    if (!veiculoId || carregando) return;
    let n = 0;
    let cronometro: ReturnType<typeof setInterval> | undefined;

    const parar = () => {
      if (cronometro !== undefined) clearInterval(cronometro);
      cronometro = undefined;
    };
    const comecar = () => {
      if (cronometro !== undefined) return;
      cronometro = setInterval(() => {
        n += 1;
        void tique(n % TIQUES_POR_RECARGA_DE_PARADAS === 0);
      }, INTERVALO_VIVO_MS);
    };
    const aoTrocarVisibilidade = () => {
      if (document.visibilityState === 'visible') {
        // Volta perguntando na hora: quem retorna à aba quer ver o agora, não esperar o próximo
        // tique para descobrir que o carro se moveu enquanto ele estava noutro lugar.
        void tique(true);
        comecar();
      } else {
        parar();
      }
    };

    if (document.visibilityState === 'visible') comecar();
    document.addEventListener('visibilitychange', aoTrocarVisibilidade);
    return () => {
      parar();
      document.removeEventListener('visibilitychange', aoTrocarVisibilidade);
    };
  }, [veiculoId, carregando, tique]);

  const aoVivo = !!veiculoId && !carregando;

  async function instalar() {
    if (!veiculo || instalando) return;
    setInstalando(true);
    const r = await api.post(`/cadastro/rastreadores/${rastreadorId}/instalar`, {
      veiculo_id: veiculo.id,
      ...(em ? { em } : {}),
    });
    setInstalando(false);
    if (!r.ok) return void toast.error(r.erro);
    toast.success('Instalado — o vínculo anterior foi fechado.');
    setVeiculo(null);
    setEm('');
    await carregar();
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Radio className="size-4 text-accent" />
          Onde está
        </CardTitle>
        <p className="text-[13px] leading-[1.5] text-text-secondary">
          O veículo em que este rastreador está instalado, o percurso e as paradas detectadas.
        </p>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        {/* Cabeçalho de situação: veículo, última posição e silêncio. */}
        <div className="flex flex-wrap items-center gap-x-6 gap-y-2 rounded-md bg-surface-2 px-3 py-2">
          <div className="flex items-center gap-2">
            <Car className="size-4 shrink-0 text-text-tertiary" />
            {instalacao ? (
              <span className="text-[15px] text-text">
                <span className="tipo-dado">{instalacao.placa ?? '—'}</span>{' '}
                <span className="text-text-secondary">
                  {[instalacao.marca, instalacao.modelo].filter(Boolean).join(' ')}
                </span>
              </span>
            ) : (
              <span className="text-[15px] text-warning">Não está instalado em nenhum veículo</span>
            )}
          </div>
          {estado && !estado.nunca_reportou && estado.silencio_seg != null && (
            <div className="flex items-center gap-2">
              <span className="tipo-rotulo">Última posição</span>
              <Chip className={estado.silencio_seg > 3600 ? 'border-warning/40 text-warning' : ''}>
                {silencioHumano(estado.silencio_seg)}
              </Chip>
              <span className="tipo-corpo-pequeno text-text-tertiary">
                {estado.total_posicoes.toLocaleString('pt-BR')} posições
              </span>
            </div>
          )}
          {/* A cadência PRATICADA, ao lado da pedida. Não há como perguntar ao aparelho qual
              intervalo está valendo nele; o espaçamento das posições é a única testemunha. Com as
              duas na tela, «o SMS de intervalo pegou?» deixa de ser suposição. */}
          {estado?.cadencia_seg != null && estado.intervalo_alvo_seg != null && (
            <div className="flex items-center gap-2">
              <span className="tipo-rotulo">Reporta a cada</span>
              <Chip
                className={
                  longeDoAlvo(estado.cadencia_seg, estado.intervalo_alvo_seg)
                    ? 'border-warning/40 text-warning'
                    : 'border-success/40 text-success'
                }
              >
                ~{cadenciaHumana(estado.cadencia_seg)}
              </Chip>
              <span className="tipo-corpo-pequeno text-text-tertiary">
                configurado para {cadenciaHumana(estado.intervalo_alvo_seg)}
                {estado.cadencia_amostras ? ` · ${estado.cadencia_amostras} intervalos` : ''}
              </span>
            </div>
          )}
          {aoVivo && (
            // Dizer que a tela se atualiza sozinha é parte do dado: sem isto, um mapa que não
            // muda há dez minutos é ambíguo — carro parado ou tela velha?
            <span className="tipo-corpo-pequeno flex items-center gap-1.5 text-text-tertiary">
              <span className="size-1.5 rounded-full bg-success" aria-hidden />
              acompanhando ao vivo
            </span>
          )}
          {estado?.nunca_reportou && (
            <span className="tipo-corpo-pequeno flex items-center gap-1.5 text-warning">
              <AlertTriangle className="size-3.5 shrink-0" />
              Nunca reportou — configure o aparelho (aba ao lado) e confira o servidor.
            </span>
          )}
        </div>

        {/* Diagnóstico do intervalo, com a instrução junto. Um número fora do esperado sem a
            frase «e é isto que se faz» só transfere o problema para quem lê. */}
        {estado?.cadencia_seg != null &&
          estado.intervalo_alvo_seg != null &&
          longeDoAlvo(estado.cadencia_seg, estado.intervalo_alvo_seg) && (
            <div className="flex gap-2 rounded-md border border-warning/40 bg-warning/5 px-3 py-2">
              <AlertTriangle className="mt-0.5 size-4 shrink-0 text-warning" />
              <p className="text-[13px] leading-[1.5] text-text-secondary">
                <span className="text-text">
                  O aparelho está reportando a cada ~{cadenciaHumana(estado.cadencia_seg)}, e não a
                  cada {cadenciaHumana(estado.intervalo_alvo_seg)}.
                </span>{' '}
                Ele está no intervalo de fábrica: o SMS que muda isso não foi enviado, ou não foi
                aceito. Enquanto for assim, o mapa vai ter mais rota calculada do que posição
                medida, porque quilômetros inteiros passam entre uma marca e a seguinte. Vá em{' '}
                <span className="text-text">Pôr para funcionar</span> e envie o comando{' '}
                <span className="text-text">Intervalo com ignição ligada</span> ao chip do
                aparelho — este número cai sozinho assim que ele aceitar.
              </p>
            </div>
          )}

        {/* Mapa */}
        {carregando ? (
          <Spinner />
        ) : pontos.length === 0 ? (
          <p className="tipo-corpo-pequeno text-text-tertiary">
            Nenhuma posição na janela escolhida.
          </p>
        ) : !CHAVE_MAPS || mapaCaiu ? (
          <p className="tipo-corpo-pequeno text-text-tertiary">
            {pontos.length.toLocaleString('pt-BR')} posições na janela. Sem
            <span className="tipo-dado"> VITE_GOOGLE_MAPS_API_KEY</span>, o mapa não desenha —
            trajeto exige Maps JS, não dá para cair no iframe como um endereço só.
          </p>
        ) : (
          <>
            <div className="h-[420px] w-full overflow-hidden rounded-md border border-border">
              <MapaTrajeto
                apiKey={CHAVE_MAPS}
                pontos={pontos}
                paradas={paradas}
                onFalhar={aoFalharMapa}
                onEstadoRota={aoEstadoRota}
                chaveEnquadramento={`${instalacao?.veiculo_id}:${janela}`}
              />
            </div>
            {/* Legenda. Separar o que foi MEDIDO do que foi CALCULADO é o ponto: a conclusão que
                se tira de «passou na frente de tal casa» muda inteiramente conforme aquilo tenha
                sido um reporte do aparelho ou o caminho que um motorista faria. */}
            <div className="tipo-corpo-pequeno flex flex-wrap items-center gap-x-5 gap-y-1 text-text-tertiary">
              <span className="flex items-center gap-2">
                <span
                  className="size-2.5 shrink-0 rounded-full border-[3px] border-accent bg-surface"
                  aria-hidden
                />
                posição reportada pelo aparelho — o dado
              </span>
              <span className="flex items-center gap-2">
                <span className="h-0.5 w-6 shrink-0 bg-accent" aria-hidden />
                rota provável, calculada pelas ruas
              </span>
              <span className="flex items-center gap-2">
                <span
                  className="h-0.5 w-6 shrink-0"
                  style={{
                    backgroundImage:
                      'repeating-linear-gradient(to right,#94a3b8 0 4px,transparent 4px 8px)',
                  }}
                  aria-hidden
                />
                trecho com mais de 3 min sem reporte — palpite fraco, clique para ver quanto
              </span>
              {rota?.indisponivel && (
                <span className="text-warning">
                  O cálculo de rotas não respondeu — os trechos estão em linha reta. Confira se a
                  <span className="tipo-dado"> Directions API</span> está habilitada na chave.
                </span>
              )}
              {!rota?.indisponivel && !!rota?.retas && (
                <span className="text-warning">
                  {rota.retas} trecho(s) sem rota calculada, desenhados em linha reta.
                </span>
              )}
              {mapIdEhDemo && (
                <span className="text-warning">
                  Sem <span className="tipo-dado">VITE_GOOGLE_MAPS_MAP_ID</span>: o mapa está no
                  Map ID de demonstração do Google, que não é para produção.
                </span>
              )}
              {pontos.length >= LIMITE_TRAJETO && (
                <span className="text-warning">
                  Mostrando as {LIMITE_TRAJETO.toLocaleString('pt-BR')} posições mais recentes da
                  janela — o começo do período está cortado.
                </span>
              )}
            </div>
          </>
        )}

        {/* Janela de tempo */}
        <div className="flex flex-wrap items-center gap-2">
          <span className="tipo-rotulo">Janela</span>
          <div className="flex gap-1 rounded-md bg-surface-2 p-1">
            {JANELAS.map((j) => (
              <Button
                key={j.chave}
                size="sm"
                variant={j.chave === janelaEscolhida.chave ? 'secundario' : 'fantasma'}
                onClick={() => escolherJanela(j.chave)}
              >
                {j.rotulo}
              </Button>
            ))}
          </div>
          {paradas.length > 0 && (
            <span className="tipo-corpo-pequeno text-text-tertiary">
              {paradas.length} paradas detectadas
            </span>
          )}
        </div>

        {/* Paradas em lista — o mapa mostra onde, a lista mostra quanto tempo. */}
        {paradas.length > 0 && (
          <div className="flex flex-col gap-2 border-t border-border pt-4">
            <span className="tipo-rotulo">Paradas</span>
            {paradas.map((p) => (
              <div key={p.id} className="flex flex-wrap items-baseline gap-2">
                <span className="tipo-dado text-text">{Math.round(p.duracao_seg / 60)} min</span>
                <span className="tipo-corpo-pequeno text-text-tertiary">
                  {new Date(p.inicio).toLocaleString('pt-BR')} · {p.lat.toFixed(4)},{' '}
                  {p.lon.toFixed(4)}
                </span>
                {p.endereco && <span className="tipo-corpo-pequeno text-text">{p.endereco}</span>}
              </div>
            ))}
          </div>
        )}

        {/* Transferir de veículo */}
        <div className="flex flex-col gap-2 border-t border-border pt-4">
          <span className="tipo-rotulo">
            {instalacao ? 'Transferir para outro veículo' : 'Instalar num veículo'}
          </span>
          <p className="tipo-corpo-pequeno text-text-tertiary">
            Instalar fecha o vínculo anterior. O histórico do veículo antigo congela — as posições
            já gravadas continuam sendo dele.
          </p>
          <ComboBuscaEntidade config={BUSCA_VEICULO} onSelecionar={setVeiculo} />
          <div className="flex flex-wrap items-end gap-3">
            <label className="flex flex-col gap-2">
              <span className="tipo-rotulo">Instalado em</span>
              <Input type="date" value={em} onChange={(e) => setEm(e.target.value)} />
            </label>
            <Button
              variant="secundario"
              disabled={!veiculo || instalando}
              onClick={() => void instalar()}
            >
              {instalando ? <Spinner /> : <MapPin />}
              {instalacao ? 'Transferir para este veículo' : 'Instalar neste veículo'}
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
